import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const PreferencesSection = ({ profile, onSave, saving }) => {
  const [preferences, setPreferences] = useState({
    dietaryRestrictions: [],
    cuisinePreferences: [],
    notificationSettings: {
      orderUpdates: true,
      promotions: true,
      newSuppliers: false
    }
  });

  const dietaryOptions = [
    { id: 'vegetarian', label: 'Vegetarian', icon: '🥗' },
    { id: 'vegan', label: 'Vegan', icon: '🌱' },
    { id: 'gluten-free', label: 'Gluten-Free', icon: '🌾' },
    { id: 'dairy-free', label: 'Dairy-Free', icon: '🥛' },
    { id: 'nut-free', label: 'Nut-Free', icon: '🥜' },
    { id: 'halal', label: 'Halal', icon: '☪️' },
    { id: 'kosher', label: 'Kosher', icon: '✡️' }
  ];

  const cuisineOptions = [
    { id: 'indian', label: 'Indian', emoji: '🍛' },
    { id: 'italian', label: 'Italian', emoji: '🍝' },
    { id: 'chinese', label: 'Chinese', emoji: '🥢' },
    { id: 'mexican', label: 'Mexican', emoji: '🌮' },
    { id: 'thai', label: 'Thai', emoji: '🍜' },
    { id: 'japanese', label: 'Japanese', emoji: '🍱' },
    { id: 'mediterranean', label: 'Mediterranean', emoji: '🥙' },
    { id: 'american', label: 'American', emoji: '🍔' }
  ];

  useEffect(() => {
    if (profile) {
      setPreferences({
        dietaryRestrictions: profile?.dietary_preferences || [],
        cuisinePreferences: profile?.favorite_cuisines || [],
        notificationSettings: profile?.notification_settings || {
          orderUpdates: true,
          promotions: true,
          newSuppliers: false
        }
      });
    }
  }, [profile]);

  const handleDietaryToggle = (dietaryId) => {
    setPreferences((prev) => ({
      ...prev,
      dietaryRestrictions: prev?.dietaryRestrictions?.includes(dietaryId)
        ? prev?.dietaryRestrictions?.filter((d) => d !== dietaryId)
        : [...prev?.dietaryRestrictions, dietaryId]
    }));
  };

  const handleCuisineToggle = (cuisineId) => {
    setPreferences((prev) => ({
      ...prev,
      cuisinePreferences: prev?.cuisinePreferences?.includes(cuisineId)
        ? prev?.cuisinePreferences?.filter((c) => c !== cuisineId)
        : [...prev?.cuisinePreferences, cuisineId]
    }));
  };

  const handleNotificationToggle = (setting) => {
    setPreferences((prev) => ({
      ...prev,
      notificationSettings: {
        ...prev?.notificationSettings,
        [setting]: !prev?.notificationSettings?.[setting]
      }
    }));
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    onSave(preferences);
  };

  return (
    <div>
      <h2 className="text-2xl font-heading font-bold text-foreground mb-6">
        Preferences
      </h2>

      <form onSubmit={handleSubmit} className="space-y-8">
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-4">
            Dietary Restrictions
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {dietaryOptions?.map((option) => (
              <button
                key={option?.id}
                type="button"
                onClick={() => handleDietaryToggle(option?.id)}
                className={`flex items-center gap-2 px-4 py-3 rounded-lg border-2 transition-smooth ${
                  preferences?.dietaryRestrictions?.includes(option?.id)
                    ? 'border-primary bg-primary/10' :'border-border hover:border-primary/50'
                }`}
              >
                <span className="text-2xl">{option?.icon}</span>
                <span className="font-medium text-sm">{option?.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div>
          <h3 className="text-lg font-semibold text-foreground mb-4">
            Cuisine Preferences
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {cuisineOptions?.map((option) => (
              <button
                key={option?.id}
                type="button"
                onClick={() => handleCuisineToggle(option?.id)}
                className={`flex items-center gap-2 px-4 py-3 rounded-lg border-2 transition-smooth ${
                  preferences?.cuisinePreferences?.includes(option?.id)
                    ? 'border-primary bg-primary/10' :'border-border hover:border-primary/50'
                }`}
              >
                <span className="text-2xl">{option?.emoji}</span>
                <span className="font-medium text-sm">{option?.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div>
          <h3 className="text-lg font-semibold text-foreground mb-4">
            Notification Settings
          </h3>
          <div className="space-y-4">
            <label className="flex items-center gap-3 p-4 rounded-lg border border-border hover:bg-muted/50 transition-smooth cursor-pointer">
              <Checkbox
                checked={preferences?.notificationSettings?.orderUpdates}
                onChange={() => handleNotificationToggle('orderUpdates')}
              />
              <div className="flex-1">
                <p className="font-medium text-foreground">Order Updates</p>
                <p className="text-sm text-muted-foreground">
                  Get notified about order status changes
                </p>
              </div>
            </label>

            <label className="flex items-center gap-3 p-4 rounded-lg border border-border hover:bg-muted/50 transition-smooth cursor-pointer">
              <Checkbox
                checked={preferences?.notificationSettings?.promotions}
                onChange={() => handleNotificationToggle('promotions')}
              />
              <div className="flex-1">
                <p className="font-medium text-foreground">Promotions & Offers</p>
                <p className="text-sm text-muted-foreground">
                  Receive special deals and discounts
                </p>
              </div>
            </label>

            <label className="flex items-center gap-3 p-4 rounded-lg border border-border hover:bg-muted/50 transition-smooth cursor-pointer">
              <Checkbox
                checked={preferences?.notificationSettings?.newSuppliers}
                onChange={() => handleNotificationToggle('newSuppliers')}
              />
              <div className="flex-1">
                <p className="font-medium text-foreground">New Suppliers</p>
                <p className="text-sm text-muted-foreground">
                  Be notified when new kitchens join near you
                </p>
              </div>
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <Button
            type="submit"
            disabled={saving}
            className="min-w-[120px]"
          >
            {saving ? (
              <>
                <Icon name="Loader2" size={16} className="animate-spin mr-2" />
                Saving...
              </>
            ) : (
              'Save Preferences'
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default PreferencesSection;